"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  clearAnalyticsCookies,
  readConsent,
  updateConsentMode,
  writeConsent,
  type ConsentStatus,
} from "@/lib/consent";

type ConsentContextType = {
  status: ConsentStatus;
  bannerOpen: boolean;
  accept: () => void;
  decline: () => void;
  closeBanner: () => void;
};

const ConsentContext = createContext<ConsentContextType | undefined>(undefined);

export function ConsentProvider({ children }: { children: ReactNode }) {
  // The server always renders "unset" + closed banner; the client reconciles on
  // mount. Same trick as I18nProvider — reading localStorage in the initializer
  // would desync SSR and hydration.
  const [status, setStatus] = useState<ConsentStatus>("unset");
  const [bannerOpen, setBannerOpen] = useState(false);

  useEffect(() => {
    const stored = readConsent();
    if (stored !== "unset") {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setStatus(stored);
    } else {
      setBannerOpen(true);
    }
  }, []);

  // Consent must be as easy to withdraw as it was to give. Rather than thread a
  // callback through every footer, we delegate: anything carrying
  // `data-consent-open` reopens the banner. That also reaches the legal pages,
  // whose markup is injected as raw HTML strings and can't take React props.
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target?.closest?.("[data-consent-open]")) return;
      event.preventDefault();
      setBannerOpen(true);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  const accept = useCallback(() => {
    writeConsent(true);
    updateConsentMode(true);
    setStatus("granted");
    setBannerOpen(false);
  }, []);

  const decline = useCallback(() => {
    writeConsent(false);
    updateConsentMode(false);
    // Covers the withdraw-after-accept path: stop measuring *and* drop what the
    // previous session already wrote.
    clearAnalyticsCookies();
    setStatus("denied");
    setBannerOpen(false);
  }, []);

  const closeBanner = useCallback(() => setBannerOpen(false), []);

  const value = useMemo(
    () => ({ status, bannerOpen, accept, decline, closeBanner }),
    [status, bannerOpen, accept, decline, closeBanner],
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent() {
  const context = useContext(ConsentContext);
  if (!context) {
    throw new Error("useConsent must be used within a ConsentProvider");
  }
  return context;
}
